import { getOnePagerReport } from '../../../../api/projectApi';
import { normalizeData } from '../../../Portfolio_R/SummaryCharts/utils/mapper_one_pager_data';
import { generatePDF } from '../../../Portfolio_R/SummaryCharts/utils/generatePdf';
import { IndicatorPayload } from '../../../../services/extendedReportData';
import { downloadOnePagerReport } from '../../../Projects/ProjectsReport/utils/Downloaders/OnePagerReportDownload';

type Sector = {
  id: number;
  code: string;
  description: string;
};

type Division = {
  id: number;
  code: string;
  name: string;
};

type OnePagerDownloadOptions = {
  indicatorPayload: IndicatorPayload | null | undefined;
  sectors: Sector[];
  divisions: Division[];
  portfolioName: string;
  onLoading?: (loading: boolean) => void;
};

const buildLabels = (
  filters: any[],
  field: string,
  items: { id: number }[],
  labelBuilder: (item: any) => string,
) => {
  const match = filters.find(filter => filter.field === field);
  if (!match || !Array.isArray(match.value)) return null;

  return match.value
    .map((id: number) => items.find(item => item.id === id))
    .filter(Boolean)
    .map(labelBuilder)
    .join(', ');
};

export const downloadOnePagerReportFromPayload = async ({
  indicatorPayload,
  sectors,
  divisions,
  portfolioName,
  onLoading,
}: OnePagerDownloadOptions) => {
  if (!indicatorPayload) return false;
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    throw new Error('offline');
  }

  onLoading?.(true);
  try {
    const data = await getOnePagerReport(indicatorPayload);
    const normalized = normalizeData(data);

    const filters = indicatorPayload?.Filters || [];
    const sectorLabels = buildLabels(filters, 'SectorId', sectors, (sector: Sector) => (
      `${sector.code} - ${sector.description}`
    ));
    const divisionLabels = buildLabels(filters, 'DivisionId', divisions, (division: Division) => (
      `${division.code} - ${division.name}`
    ));

    const filtersAppliedForPdf = [null, sectorLabels, divisionLabels];

    await generatePDF({
      loan: normalized.loan,
      tcp: normalized.tcp,
      igr: normalized.igr,
      generalData: normalized.generalData,
      filtersApplied: filtersAppliedForPdf,
      onLoading: () => null,
      portfolioName,
    });

    return true;
  } finally {
    onLoading?.(false);
  }
};

export const downloadOnePagerReportFromProject = async ({
  projectId,
  onLoading,
}: {
  projectId: number | string | null | undefined;
  onLoading?: (loading: boolean) => void;
}) => {
  if (!projectId) return false;
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    throw new Error('offline');
  }

  onLoading?.(true);
  try {
    await downloadOnePagerReport(projectId, onLoading);
    return true;
  } finally {
    onLoading?.(false);
  }
};
